import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Clock, ChevronDown, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PerformanceTracker = ({ refreshTrigger = 0 }) => {
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);
  const [period, setPeriod] = useState("all");
  const [trackingMode, setTrackingMode] = useState("auto"); // "auto", "manual", or "backtest"
  const [expandedRows, setExpandedRows] = useState({}); // Track which rows are expanded

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        let endpoint;
        if (trackingMode === "manual") {
          endpoint = `${API}/manual/performance`;
        } else if (trackingMode === "backtest") {
          endpoint = `${API}/backtest/performance`;
        } else {
          endpoint = `${API}/performance`;
        }
        const response = await axios.get(`${endpoint}?limit=${limit}&period=${period}`);
        console.log(`[PerformanceTracker] Fetched ${response.data.length} items from ${endpoint}`);
        if (response.data.length > 0) {
          console.log('[PerformanceTracker] First item market regime:', {
            coin: response.data[0].coin,
            market_trend: response.data[0].market_trend,
            market_volatility: response.data[0].market_volatility,
            market_trend_strength: response.data[0].market_trend_strength
          });
        }
        setPerformance(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching performance:", error);
        setLoading(false);
      }
    };

    fetchPerformance();
    const interval = setInterval(fetchPerformance, 10000); // Refresh every 10 seconds for real-time updates

    return () => clearInterval(interval);
  }, [limit, period, trackingMode, refreshTrigger]); // Added refreshTrigger to dependencies

  // Helper to format price with dynamic precision
  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'N/A';
    if (price < 0.0001) return `$${price.toFixed(10)}`;
    if (price < 1) return `$${price.toFixed(6)}`;
    if (price < 100) return `$${price.toFixed(4)}`;
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Check if a row has metadata to expand
  const hasMetadata = (item) => {
    return item.base_confidence !== undefined || 
           item.fear_greed_value !== undefined || 
           item.market_trend !== undefined;
  };

  if (loading) {
    return (
      <div className="glass-card rounded-xl p-6 h-64 flex items-center justify-center" data-testid="performance-loading">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-2 text-xs text-muted-foreground">Loading performance...</p>
        </div>
      </div>
    );
  }

  const formatDateTime = (timestamp) => {
    if (!timestamp) return { date: 'N/A', time: '' };
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    if (minutes < 60) {
      return `${Math.round(minutes)}m`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    } else {
      const days = Math.floor(minutes / 1440);
      const hours = Math.floor((minutes % 1440) / 60);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }
  };

  return (
    <div className="glass-card rounded-xl p-6 border-white/10 animate-fade-in-up mt-6" data-testid="performance-tracker">
      <div className="flex items-center gap-4 mb-6">
        <TrendingUp className="w-5 h-5 text-[#00FF94]" />
        <h3 className="text-lg font-semibold heading-font">
          {trackingMode === "manual" ? "Manual" : trackingMode === "backtest" ? "Backtest" : "Automated"} Signal Performance
        </h3>
        
        <div className="ml-auto flex items-center gap-4">
          {/* Tracking Mode Toggle */}
          <select
            value={trackingMode}
            onChange={(e) => setTrackingMode(e.target.value)}
            className="px-3 py-1.5 text-sm bg-primary/20 border border-primary/30 rounded-lg text-white hover:bg-primary/30 transition-all cursor-pointer focus:outline-none focus:border-primary font-semibold"
          >
            <option value="auto">🤖 Auto Trading</option>
            <option value="manual">🎯 Manual Trading</option>
            <option value="backtest">📊 Backtest</option>
          </select>
          
          {/* Period Filter */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 text-sm bg-black/30 border border-white/10 rounded-lg text-white hover:bg-black/40 transition-all cursor-pointer focus:outline-none focus:border-primary"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="all">All Time</option>
          </select>
          
          {/* Show More Dropdown */}
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-3 py-1.5 text-sm bg-black/30 border border-white/10 rounded-lg text-white hover:bg-black/40 transition-all cursor-pointer focus:outline-none focus:border-primary"
          >
            <option value={25}>Last 25</option>
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
            <option value={500}>All</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 text-xs text-muted-foreground">
              <th className="text-left py-3 px-2 font-medium w-8"></th>
              <th className="text-left py-3 px-4 font-medium">Entry Time</th>
              <th className="text-left py-3 px-4 font-medium">Coin</th>
              <th className="text-left py-3 px-4 font-medium">Signal</th>
              <th className="text-left py-3 px-4 font-medium">Confidence</th>
              <th className="text-left py-3 px-4 font-medium">Entry</th>
              <th className="text-left py-3 px-4 font-medium">Exit/Current</th>
              <th className="text-left py-3 px-4 font-medium">P/L</th>
              <th className="text-left py-3 px-4 font-medium">Size</th>
              <th className="text-left py-3 px-4 font-medium">Accuracy</th>
              <th className="text-left py-3 px-4 font-medium">Closed At</th>
              <th className="text-left py-3 px-4 font-medium">Duration</th>
              <th className="text-left py-3 px-4 font-medium">Position</th>
              <th className="text-left py-3 px-4 font-medium">Mode</th>
            </tr>
          </thead>
          <tbody>
            {performance.length === 0 ? (
              <tr>
                <td colSpan="14" className="text-center py-8 text-muted-foreground">
                  No signals found for this period.
                </td>
              </tr>
            ) : (
              performance.map((item, index) => {
                const dt = formatDateTime(item.timestamp);
                const statusInfo = item.status || 'legacy';
                const isExpanded = expandedRows[item.id];
                const showExpandButton = hasMetadata(item);
                return (
                <React.Fragment key={item.id}>
                <tr
                  className={`border-b border-white/5 hover:bg-white/5 transition-colors ${showExpandButton ? 'cursor-pointer' : ''}`}
                  data-testid={`performance-row-${index}`}
                  onClick={() => showExpandButton && toggleRowExpansion(item.id)}
                >
                  <td className="py-3 px-2">
                    {showExpandButton ? (
                      <button className="text-muted-foreground hover:text-white transition-colors">
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </button>
                    ) : null}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-xs">
                      <div className="text-data font-medium">{dt.date}</div>
                      <div className="text-muted-foreground">{dt.time}</div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-semibold text-data" data-testid={`perf-coin-${index}`}>{item.coin}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold ${
                        item.signal === "BUY"
                          ? "text-[#00FF94] bg-[#00FF94]/10"
                          : item.signal === "SELL"
                          ? "text-[#FF0055] bg-[#FF0055]/10"
                          : "text-[#FFD600] bg-[#FFD600]/10"
                      }`}
                      data-testid={`perf-signal-${index}`}
                    >
                      {item.signal}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                        item.confidence >= 75
                          ? "text-[#00FF94] bg-[#00FF94]/10"
                          : item.confidence >= 60
                          ? "text-yellow-400 bg-yellow-400/10"
                          : "text-[#FF0055] bg-[#FF0055]/10"
                      }`}
                      data-testid={`perf-confidence-${index}`}
                    >
                      {item.confidence || 0}%
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-data text-sm" data-testid={`perf-entry-${index}`}>
                      ${item.price_at_signal < 0.0001 
                        ? item.price_at_signal.toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 10 })
                        : item.price_at_signal < 1 
                        ? item.price_at_signal.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
                        : item.price_at_signal < 100
                        ? item.price_at_signal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
                        : item.price_at_signal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      }
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-data text-sm" data-testid={`perf-current-${index}`}>
                      ${item.current_price < 0.0001 
                        ? item.current_price.toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 10 })
                        : item.current_price < 1 
                        ? item.current_price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
                        : item.current_price < 100
                        ? item.current_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
                        : item.current_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      }
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-center">
                      <div className={`text-data font-bold ${item.profit_loss > 0 ? "text-[#00FF94]" : "text-[#FF0055]"}`} data-testid={`perf-pl-${index}`}>
                        {item.profit_loss > 0 ? "+" : ""}${item.profit_loss.toFixed(2)}
                      </div>
                      <div className={`text-xs ${item.price_change_percent > 0 ? "text-[#00FF94]" : "text-[#FF0055]"}`}>
                        {item.price_change_percent > 0 ? "+" : ""}{item.price_change_percent}%
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-data text-sm" data-testid={`perf-size-${index}`}>
                      ${item.position_size ? item.position_size.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '500.00'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {/* Show "Pending" for open positions with exactly 0 price movement (both % and $) */}
                    {item.status === 'open' && Math.abs(item.price_change_percent || 0) === 0 && Math.abs(item.profit_loss || 0) === 0 ? (
                      <span
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-yellow-500/20 text-yellow-400"
                        data-testid={`perf-accuracy-${index}`}
                      >
                        ⏳ Pending
                      </span>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                          item.is_correct
                            ? "bg-[#00FF94]/20 text-[#00FF94]"
                            : "bg-[#FF0055]/20 text-[#FF0055]"
                        }`}
                        data-testid={`perf-accuracy-${index}`}
                      >
                        {item.is_correct ? "✓ Correct" : "✗ Wrong"}
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {item.exit_timestamp ? (
                      <div className="text-xs">
                        <div className="text-data font-medium">{formatDateTime(item.exit_timestamp).date}</div>
                        <div className="text-muted-foreground">{formatDateTime(item.exit_timestamp).time}</div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">--</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {item.holding_duration_minutes ? (
                      <span className="text-xs text-data font-medium" title={`${item.holding_duration_minutes?.toFixed(1)} minutes`}>
                        {formatDuration(item.holding_duration_minutes)}
                      </span>
                    ) : item.status === 'open' ? (
                      <span className="text-xs text-blue-400">Active</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">--</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold whitespace-nowrap ${
                        statusInfo === "closed_profit"
                          ? "bg-[#00FF94]/20 text-[#00FF94]"
                          : statusInfo === "closed_loss"
                          ? "bg-[#FF0055]/20 text-[#FF0055]"
                          : statusInfo === "closed_time"
                          ? "bg-yellow-400/20 text-yellow-400"
                          : statusInfo === "closed_stopped"
                          ? "bg-purple-400/20 text-purple-400"
                          : statusInfo === "closed_stale"
                          ? "bg-orange-400/20 text-orange-400"
                          : statusInfo === "closed_manual"
                          ? "bg-cyan-400/20 text-cyan-400"
                          : statusInfo === "closed_force"
                          ? "bg-red-400/20 text-red-400"
                          : statusInfo === "open"
                          ? "bg-blue-400/20 text-blue-400"
                          : "bg-gray-400/20 text-gray-400"
                      }`}
                      data-testid={`perf-position-${index}`}
                    >
                      {statusInfo === "closed_profit" && "🎯 Profit"}
                      {statusInfo === "closed_loss" && "🛑 Loss"}
                      {statusInfo === "closed_time" && "⏰ Time"}
                      {statusInfo === "closed_stopped" && "⏹️ Stopped"}
                      {statusInfo === "closed_stale" && "🔒 Stale"}
                      {statusInfo === "closed_manual" && "✅ Closed"}
                      {statusInfo === "closed_force" && "⚡ Force"}
                      {statusInfo === "closed_take_profit" && "🎯 TP Hit"}
                      {statusInfo === "closed_stop_loss" && "🛑 SL Hit"}
                      {statusInfo === "closed_max_hold" && "⏰ Max Hold"}
                      {statusInfo === "closed_backtest" && "📊 Backtest"}
                      {statusInfo === "open" && "📊 Open"}
                      {statusInfo === "legacy" && "📜 Legacy"}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        item.mode === "manual"
                          ? "text-blue-400 bg-blue-400/10"
                          : item.mode === "automated_ui"
                          ? "text-green-400 bg-green-400/10"
                          : item.mode === "automated_cron"
                          ? "text-purple-400 bg-purple-400/10"
                          : item.mode === "Backtest"
                          ? "text-indigo-400 bg-indigo-400/10"
                          : "text-gray-400 bg-gray-400/10"
                      }`}
                    >
                      {item.mode === "manual" 
                        ? "Manual" 
                        : item.mode === "automated_ui" 
                        ? "UI" 
                        : item.mode === "automated_cron"
                        ? "Cron"
                        : item.mode === "Backtest"
                        ? "📊 Backtest"
                        : "Old"}
                    </span>
                  </td>
                </tr>
                {/* Expandable Metadata Row */}
                {isExpanded && showExpandButton && (
                  <tr className="bg-black/30">
                    <td colSpan="14" className="py-4 px-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
                        {/* Confidence Breakdown */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-primary flex items-center gap-2">
                            <span className="w-2 h-2 bg-primary rounded-full"></span>
                            Confidence Breakdown
                          </h4>
                          <div className="space-y-2 pl-4">
                            {item.base_confidence !== undefined && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Base Confidence:</span>
                                <span className="text-data font-medium">{item.base_confidence?.toFixed(1)}%</span>
                              </div>
                            )}
                            {item.fear_greed_adjustment !== undefined && item.fear_greed_adjustment !== 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Fear & Greed:</span>
                                <span className={`font-medium ${item.fear_greed_adjustment > 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                                  {item.fear_greed_adjustment > 0 ? '+' : ''}{item.fear_greed_adjustment?.toFixed(1)}%
                                </span>
                              </div>
                            )}
                            {/* Show Counter-Trend Penalty (new soft filter) */}
                            {item.trend_penalty !== undefined && item.trend_penalty !== null && item.trend_penalty > 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Counter-Trend Penalty:</span>
                                <span className="font-medium text-[#FF0055]">
                                  -{item.trend_penalty.toFixed(1)}%
                                </span>
                              </div>
                            )}
                            {/* Legacy: Show old Trend Alignment for historical signals */}
                            {item.trend_alignment_adjustment !== undefined && item.trend_alignment_adjustment !== null && item.trend_alignment_adjustment !== 0 && !item.trend_penalty && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Trend Alignment:</span>
                                <span className={`font-medium ${item.trend_alignment_adjustment > 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                                  {item.trend_alignment_adjustment > 0 ? '+' : ''}{item.trend_alignment_adjustment.toFixed(1)}%
                                </span>
                              </div>
                            )}
                            {item.volatility_adjustment !== undefined && item.volatility_adjustment !== null && item.volatility_adjustment !== 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Volatility Adj:</span>
                                <span className={`font-medium ${item.volatility_adjustment > 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                                  {item.volatility_adjustment > 0 ? '+' : ''}{item.volatility_adjustment.toFixed(1)}%
                                </span>
                              </div>
                            )}
                            {item.trend_strength_adjustment !== undefined && item.trend_strength_adjustment !== null && item.trend_strength_adjustment !== 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Trend Strength:</span>
                                <span className={`font-medium ${item.trend_strength_adjustment > 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                                  {item.trend_strength_adjustment > 0 ? '+' : ''}{item.trend_strength_adjustment.toFixed(1)}%
                                </span>
                              </div>
                            )}
                            {item.score_agreement_adjustment !== undefined && item.score_agreement_adjustment !== null && item.score_agreement_adjustment !== 0 && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Score Agreement:</span>
                                <span className={`font-medium ${item.score_agreement_adjustment > 0 ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                                  {item.score_agreement_adjustment > 0 ? '+' : ''}{item.score_agreement_adjustment.toFixed(1)}%
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between border-t border-white/10 pt-2 mt-2">
                              <span className="text-muted-foreground font-medium">Final Confidence:</span>
                              <span className="text-data font-bold">{item.confidence}%</span>
                            </div>
                          </div>
                        </div>

                        {/* AI Predictions */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-blue-400 flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                            AI Model Predictions
                          </h4>
                          <div className="space-y-2 pl-4">
                            {item.lstm_prediction !== undefined && item.lstm_prediction !== null && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">LSTM Predicted Price:</span>
                                <span className={`font-medium ${item.lstm_prediction > item.price_at_signal ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                                  {item.lstm_prediction > item.price_at_signal ? '▲' : '▼'} ${item.lstm_prediction < 1 
                                    ? item.lstm_prediction.toFixed(6) 
                                    : item.lstm_prediction < 100 
                                    ? item.lstm_prediction.toFixed(2)
                                    : item.lstm_prediction.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            )}
                            {item.gb_prediction !== undefined && item.gb_prediction !== null && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">GB Predicted Price:</span>
                                <span className={`font-medium ${item.gb_prediction > item.price_at_signal ? 'text-[#00FF94]' : 'text-[#FF0055]'}`}>
                                  {item.gb_prediction > item.price_at_signal ? '▲' : '▼'} ${item.gb_prediction < 1 
                                    ? item.gb_prediction.toFixed(6) 
                                    : item.gb_prediction < 100 
                                    ? item.gb_prediction.toFixed(2)
                                    : item.gb_prediction.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            )}
                            {item.fear_greed_value !== undefined && item.fear_greed_value !== null && (
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Fear & Greed Index:</span>
                                <span className={`font-medium ${
                                  item.fear_greed_value >= 60 ? 'text-[#00FF94]' : 
                                  item.fear_greed_value <= 40 ? 'text-[#FF0055]' : 'text-yellow-400'
                                }`}>
                                  {item.fear_greed_value} ({item.fear_greed_value >= 60 ? 'Greed' : item.fear_greed_value <= 40 ? 'Fear' : 'Neutral'})
                                </span>
                              </div>
                            )}
                            {(item.lstm_prediction === undefined || item.lstm_prediction === null) && 
                             (item.gb_prediction === undefined || item.gb_prediction === null) && 
                             (item.fear_greed_value === undefined || item.fear_greed_value === null) && (
                              <span className="text-muted-foreground text-xs">No AI prediction data available</span>
                            )}
                          </div>
                        </div>

                        {/* Market Regime Data */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-purple-400 flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
                            Market Regime
                          </h4>
                          <div className="space-y-2 pl-4 text-sm">
                            <p>
                              <span className="text-muted-foreground">Trend: </span>
                              <strong className={item.market_trend === 'bull' ? 'text-[#00FF94]' : item.market_trend === 'bear' ? 'text-[#FF0055]' : 'text-yellow-400'}>
                                {item.market_trend ? item.market_trend.charAt(0).toUpperCase() + item.market_trend.slice(1) : 'N/A'}
                              </strong>
                            </p>
                            <p>
                              <span className="text-muted-foreground">Vol. Level: </span>
                              <strong className={item.market_volatility === 'low' ? 'text-[#00FF94]' : item.market_volatility === 'high' ? 'text-[#FF0055]' : 'text-yellow-400'}>
                                {item.market_volatility ? item.market_volatility.charAt(0).toUpperCase() + item.market_volatility.slice(1) : 'N/A'}
                              </strong>
                            </p>
                            <p>
                              <span className="text-muted-foreground">Trend Strength: </span>
                              <strong className="text-data">{item.market_trend_strength != null ? (item.market_trend_strength * 100).toFixed(1) + '%' : 'N/A'}</strong>
                            </p>
                          </div>
                        </div>

                        {/* MTF Entry Timing Data - 4th Column */}
                        <div className="space-y-3">
                          <h4 className="font-semibold text-cyan-400 flex items-center gap-2">
                            <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                            MTF Entry Timing
                          </h4>
                          <div className="space-y-2 pl-4 text-sm">
                            {/* Status with color coding */}
                            <p>
                              <span className="text-muted-foreground">Status: </span>
                              <strong className={
                                item.mtf_status === 'EXECUTE' ? 'text-[#00FF94]' : 
                                item.mtf_status === 'WAIT' ? 'text-yellow-400' : 
                                item.mtf_status === 'SKIP' ? 'text-[#FF0055]' : 'text-muted-foreground'
                              }>
                                {item.mtf_status || 'N/A'}
                              </strong>
                            </p>
                            
                            {/* Hourly Section */}
                            <div className="mt-2">
                              <span className="text-muted-foreground text-xs">📊 Hourly (48h)</span>
                              <div className="pl-2 space-y-1 mt-1">
                                <p>
                                  <span className="text-muted-foreground">Aligned: </span>
                                  <strong className={item.mtf_hourly_aligned ? 'text-[#00FF94]' : 'text-[#FF0055]'}>
                                    {item.mtf_hourly_aligned !== undefined ? (item.mtf_hourly_aligned ? '✓ Yes' : '✗ No') : 'N/A'}
                                  </strong>
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Conditions: </span>
                                  <strong className="text-data">{item.mtf_hourly_conditions || 'N/A'}</strong>
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Trend: </span>
                                  <strong className={
                                    item.mtf_hourly_trend === 'bullish' ? 'text-[#00FF94]' : 
                                    item.mtf_hourly_trend === 'bearish' ? 'text-[#FF0055]' : 'text-yellow-400'
                                  }>
                                    {item.mtf_hourly_trend || 'N/A'}
                                  </strong>
                                </p>
                              </div>
                            </div>
                            
                            {/* 5-Min Section */}
                            <div className="mt-2">
                              <span className="text-muted-foreground text-xs">⚡ 5-Min (4h)</span>
                              <div className="pl-2 space-y-1 mt-1">
                                <p>
                                  <span className="text-muted-foreground">Momentum: </span>
                                  <strong className={
                                    item.mtf_5min_momentum === 'bullish' ? 'text-[#00FF94]' : 
                                    item.mtf_5min_momentum === 'bearish' ? 'text-[#FF0055]' : 'text-yellow-400'
                                  }>
                                    {item.mtf_5min_momentum || 'N/A'}
                                  </strong>
                                </p>
                                <p>
                                  <span className="text-muted-foreground">RSI: </span>
                                  <strong className="text-data">
                                    {item.mtf_5min_rsi !== undefined && item.mtf_5min_rsi !== null ? item.mtf_5min_rsi.toFixed(1) : 'N/A'}
                                  </strong>
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Range: </span>
                                  <strong className="text-data">
                                    {item.mtf_5min_range !== undefined && item.mtf_5min_range !== null ? `${(item.mtf_5min_range * 100).toFixed(0)}%` : 'N/A'}
                                  </strong>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformanceTracker;// Build timestamp: 1767306340
